import React from 'react'
import classNames from 'classnames'
import { CaretRightIcon } from '@/atoms/Icons/CaretRightIcon'
import { AsH3, CaptionSM } from '@/atoms/Text'
import { useFormatDuration } from '@/utils/EpisodeUtil'
import { useTranslate } from '@/utils/translate/translate-client'

type EpisodeDescriptionProps = {
  title?: string
  subtitle?: string
  displayDescription?: boolean
  description?: string
  href: string
  className?: string
  episodeNumber?: number
  duration?: number
  projectType?: 'series' | 'movie' | 'special' | 'podcast' | undefined
}

export const EpisodeDescription: React.FC<EpisodeDescriptionProps> = ({
  title,
  subtitle,
  displayDescription,
  description,
  href,
  className,
  episodeNumber,
  duration,
  projectType,
}) => {
  const { t } = useTranslate('watch')
  const formatDuration = useFormatDuration(t)
  const isSeries = projectType === 'series'

  const episodeLabel = episodeNumber ? `${t('episode', 'Episode')} ${episodeNumber}` : null
  const durationLabel = typeof duration === 'number' ? formatDuration(duration) : undefined
  const seriesDisplayTitle = title?.trim() ? title : subtitle

  return (
    <div className={classNames('space-between flex w-full flex-row items-center h-full gap-x-4 lg:gap-x-0', className)}>
      <div className="flex w-full flex-col gap-[6px]">
        {episodeLabel && isSeries && (
          <CaptionSM color="core-gray-500" className="md:photon-caption-md lg:photon-caption-lg">
            {episodeLabel}
          </CaptionSM>
        )}
        {!isSeries && title && (
          <CaptionSM color="core-gray-500" className="md:photon-caption-md lg:photon-caption-lg">
            {title}
          </CaptionSM>
        )}
        {isSeries && seriesDisplayTitle && (
          <CaptionSM
            as={AsH3}
            weight="bold"
            color="white"
            aria-label={seriesDisplayTitle}
            className="md:photon-title-md"
          >
            {seriesDisplayTitle}
          </CaptionSM>
        )}
        {!isSeries && subtitle && (
          <CaptionSM as={AsH3} weight="bold" color="white" aria-label={subtitle} className="md:photon-title-md">
            {subtitle}
          </CaptionSM>
        )}
        {durationLabel && (
          <CaptionSM color="core-gray-500" className="md:photon-paragraph-md">
            {durationLabel}
          </CaptionSM>
        )}
        {displayDescription && description && (
          <div className="hidden md:flex">
            <CaptionSM
              color="core-gray-500"
              aria-label={href.includes('/community/stories') ? 'video-story-description' : 'episode-description'}
              className="lg:photon-paragraph-md line-clamp-4 text-core-gray-500 2xl:line-clamp-6"
            >
              {description}
            </CaptionSM>
          </div>
        )}
      </div>
      <a className="lg:hidden" href={href}>
        <CaretRightIcon size={24} color="white" />
      </a>
    </div>
  )
}
